import { ExpenseModel } from '../../reduxToolkit/expensesSlice'
import { present } from '../util/present'

/**
 * The lifecycle state an expense is shown in on the triage screen.
 *  - 'draft'    auto-imported from Plaid, not yet reviewed (is_draft).
 *  - 'attached' the catch-all for any non-draft, non-closed expense — this includes
 *               expenses assigned to an open incident AND standalone non-draft
 *               expenses with no incident yet (both are still in-flight / "open").
 *  - 'closed'   submitted for reimbursement OR belongs to a closed incident.
 *
 * 'draft' + 'attached' together make up the "open" set used by the filter.
 */
export type ExpenseState = 'draft' | 'attached' | 'closed'

type ExpenseStateInput = Pick<ExpenseModel, 'is_draft' | 'submitted_at' | 'incident_id'>

/**
 * Derive an expense's display state. `closedIncidentIds` is the set of incident
 * ids whose incident has a `closed_at` — computed once by the caller so this stays
 * a cheap pure function usable both for filtering and per-row rendering.
 */
export function deriveExpenseState(
  expense: ExpenseStateInput,
  closedIncidentIds: Set<string>,
): ExpenseState {
  if (expense.is_draft === true) {
    return 'draft'
  }
  if (present(expense.submitted_at)) {
    return 'closed'
  }
  if (present(expense.incident_id) && closedIncidentIds.has(expense.incident_id)) {
    return 'closed'
  }
  return 'attached'
}

export interface ExpenseStateBadge {
  label: string
  /** Bootstrap contextual suffix, also used as a CSS class hook. */
  variant: 'info' | 'primary' | 'secondary'
}

/**
 * The badge label + color for an already-derived expense state. Takes the state
 * (rather than re-deriving it) so callers that also need the state — like the
 * triage row, which uses it for the row's accent class — compute it only once.
 * The 'closed' state distinguishes a submitted expense ("SUBMITTED") from one
 * whose incident was closed ("CLOSED").
 */
export function expenseStateBadge(
  state: ExpenseState,
  expense: Pick<ExpenseModel, 'submitted_at'>,
): ExpenseStateBadge {
  switch (state) {
    case 'draft':
      return { label: 'DRAFT', variant: 'info' }
    case 'attached':
      return { label: 'OPEN', variant: 'primary' }
    case 'closed':
      return {
        label: present(expense.submitted_at) ? 'SUBMITTED' : 'CLOSED',
        variant: 'secondary',
      }
  }
}
